import React, {useState} from 'react';
import {motion} from 'framer-motion';
import StatCard from '../components/StatCard';
import AnalysisProgress from '../components/AnalysisProgress';
import ResultsTable from '../components/ResultsTable';
import ActivityAnalysis from '../components/ActivityAnalysis';
import FileUpload from '../components/FileUpload';
import ManualWebsiteEntry from '../components/ManualWebsiteEntry';
import ConfigPanel from '../components/ConfigPanel';
import SessionManager from '../components/SessionManager';
import ServiceSelector from '../components/ServiceSelector';
import OpenerStyleSettings from '../components/OpenerStyleSettings';
import BackgroundTaskManager from '../components/BackgroundTaskManager';
import BackgroundProcessingTest from '../components/BackgroundProcessingTest';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {FiAlertTriangle, FiSettings, FiSliders} = FiIcons;

const Dashboard = ({
  analyticData,
  results,
  filteredResults,
  websites,
  isAnalyzing,
  progress,
  activeFilter,
  setActiveFilter,
  entryMethod,
  handleFileUpload,
  handleManualWebsites,
  handleStartAnalysis,
  handleConfigSave,
  config,
  clearAllResults,
  sessionName,
  setSessionName,
  onSessionSelect,
  currentSessionId,
  selectedService,
  setSelectedService,
  selectedSubService,
  setSelectedSubService,
  openerConfig,
  setOpenerConfig,
  onEmailsExtracted,
  scrapingOptions,
  setScrapingOptions
}) => {
  const [showSessionInput, setShowSessionInput] = useState(false);
  const [showOpenerSettings, setShowOpenerSettings] = useState(false);

  const handleAnalysisStart = () => {
    if (!sessionName && !showSessionInput) {
      setShowSessionInput(true);
      return;
    }
    handleStartAnalysis();
    setShowSessionInput(false);
  };

  // Get service display name
  const getServiceDisplayName = () => {
    if (selectedService === 'website') return 'Website Redesign';
    if (selectedService === 'onboarding') {
      return selectedSubService === 'apollo' ? 'Apollo.io Onboarding' : 'Instantly.ai Onboarding';
    }
    if (selectedService === 'cold_email') return 'Cold Email System';
    return 'Website Analysis';
  };

  return (
    <div className="dashboard-layout">
      <div className="stats-row">
        <StatCard
          icon="Globe"
          color="green"
          title="Total Websites"
          value={analyticData.totalWebsites > 0 ? analyticData.totalWebsites : '0'}
          subtitle="Analyzed"
        />
        <StatCard
          icon="Check"
          color="pink"
          title="High Potential"
          value={analyticData.activeAnalyses > 0 ? analyticData.activeAnalyses : '0'}
          subtitle="Score 7+"
        />
        <StatCard
          icon="Zap"
          color="orange"
          title="Completed"
          value={analyticData.completedAnalyses > 0 ? analyticData.completedAnalyses : '0'}
          subtitle="Analyses"
        />
        <StatCard
          icon="Clock"
          color="blue"
          title="Recent"
          value={analyticData.recentAnalyses > 0 ? analyticData.recentAnalyses : '0'}
          subtitle="Last 7 Days"
        />
      </div>

      <div className="main-grid">
        <div className="content-section">
          <SessionManager
            onSessionSelect={onSessionSelect}
            currentSessionId={currentSessionId}
          />

          <ServiceSelector
            selectedService={selectedService}
            setSelectedService={setSelectedService}
            selectedSubService={selectedSubService}
            setSelectedSubService={setSelectedSubService}
          />

          {/* Session Data Display */}
          {currentSessionId && websites && websites.length > 0 && websites[0]?.source === 'verified_leads' && (
            <div className="session-data-section">
              <div className="section-header">
                <h3>Session Leads Data</h3>
                <span className="data-count">{websites.length} verified leads</span>
              </div>
              <div className="leads-preview">
                {websites.slice(0, 5).map((lead, index) => (
                  <div key={lead.leadId || lead.email || `lead-${index}`} className="lead-preview-item">
                    <div className="lead-info">
                      <div className="lead-name">
                        {lead.firstName} {lead.lastName}
                      </div>
                      <div className="lead-details">
                        <span className="company">{lead.company}</span>
                        {lead.jobTitle && <span className="job-title"> • {lead.jobTitle}</span>}
                      </div>
                      <div className="lead-contact">
                        <span className="email">{lead.email}</span>
                        {lead.website && <span className="website"> • {lead.website}</span>}
                      </div>
                    </div>
                  </div>
                ))}
                {websites.length > 5 && (
                  <div className="more-leads-indicator">
                    +{websites.length - 5} more leads
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="settings-toggles">
            {/* Opener Settings Toggle */}
            <button
              className="settings-toggle-button"
              onClick={() => setShowOpenerSettings(!showOpenerSettings)}
            >
              <SafeIcon
                icon={showOpenerSettings ? FiSliders : FiSettings}
                className="settings-icon"
              />
              <span>{showOpenerSettings ? 'Hide Opener Settings' : 'Show Opener Settings'}</span>
            </button>
          </div>

          {/* Opener Style Settings */}
          {showOpenerSettings && openerConfig && setOpenerConfig && (
            <OpenerStyleSettings
              openerConfig={openerConfig}
              setOpenerConfig={setOpenerConfig}
              selectedService={selectedService}
              selectedSubService={selectedSubService}
            />
          )}

          <div className="section-header">
            <h2>Recent Website Analyses for {getServiceDisplayName()}</h2>
            <div className="filter-tabs">
              <span
                className={`filter-tab ${activeFilter === 'all' ? 'active' : ''}`}
                onClick={() => setActiveFilter('all')}
              >
                All
              </span>
              <span
                className={`filter-tab ${activeFilter === 'high' ? 'active' : ''}`}
                onClick={() => setActiveFilter('high')}
              >
                High Score
              </span>
              <span
                className={`filter-tab ${activeFilter === 'low' ? 'active' : ''}`}
                onClick={() => setActiveFilter('low')}
              >
                Low Score
              </span>
            </div>
          </div>

          {isAnalyzing ? (
            <AnalysisProgress progress={progress} />
          ) : websites.length > 0 ? (
            <motion.div
              className="ready-card"
              initial={{opacity: 0, y: 10}}
              animate={{opacity: 1, y: 0}}
              transition={{duration: 0.3}}
            >
              <div className="ready-content">
                <div>
                  <h3>Ready to analyze for {getServiceDisplayName()}</h3>
                  <p>{websites.length} websites loaded</p>
                </div>

                {showSessionInput ? (
                  <div className="session-input-container">
                    <div className="input-wrapper">
                      <input
                        type="text"
                        value={sessionName}
                        onChange={(e) => setSessionName(e.target.value)}
                        placeholder="Enter session name (required)"
                        className="form-input"
                        autoFocus
                      />
                      {!sessionName && (
                        <div className="session-warning">
                          <SafeIcon icon={FiAlertTriangle} className="warning-icon" />
                          <span>Session name is required</span>
                        </div>
                      )}
                    </div>
                    <button
                      onClick={handleAnalysisStart}
                      className="analyze-button"
                      disabled={!sessionName}
                    >
                      Start Analysis
                    </button>
                  </div>
                ) : (
                  <button onClick={handleAnalysisStart} className="analyze-button">
                    Start Analysis
                  </button>
                )}
              </div>
            </motion.div>
          ) : filteredResults.length > 0 ? (
            <ResultsTable results={filteredResults.slice(0, 4)} />
          ) : (
            <div className="empty-state">
              <p>
                {activeFilter !== 'all'
                  ? `No ${activeFilter === 'high' ? 'high score' : 'low score'} website analyses available.`
                  : 'No websites analyzed yet. Add websites using one of the methods below.'}
              </p>
            </div>
          )}

          <div className="upload-section">
            {/* Scraping Options */}
            <div className="scraping-options" style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
              <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', fontWeight: '600', color: '#495057' }}>Scraping Options</h4>
              <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#6c757d', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={scrapingOptions.scrapeWebsite}
                    onChange={(e) => setScrapingOptions(prev => ({ ...prev, scrapeWebsite: e.target.checked }))}
                    style={{ margin: 0 }}
                  />
                  Scrape Website
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#6c757d', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={scrapingOptions.scrapeLinkedIn}
                    onChange={(e) => setScrapingOptions(prev => ({ ...prev, scrapeLinkedIn: e.target.checked }))}
                    style={{ margin: 0 }}
                  />
                  Scrape LinkedIn
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#6c757d', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={scrapingOptions.scrapeTalkingPoints}
                    onChange={(e) => setScrapingOptions(prev => ({ ...prev, scrapeTalkingPoints: e.target.checked }))}
                    style={{ margin: 0 }}
                  />
                  Scrape Talking Points
                </label>
              </div>
              <p style={{ margin: '8px 0 0 0', fontSize: '11px', color: '#6c757d', fontStyle: 'italic' }}>
                Select which data sources to scrape for personalized opener generation
              </p>
            </div>

            <div className="upload-methods">
              {entryMethod !== 'manual' && (
                <FileUpload 
                  onFileUpload={handleFileUpload} 
                  disabled={isAnalyzing} 
                  onEmailsExtracted={onEmailsExtracted}
                  scrapingOptions={scrapingOptions}
                />
              )}
              {entryMethod !== 'csv' && (
                <ManualWebsiteEntry onWebsitesAdd={handleManualWebsites} disabled={isAnalyzing} />
              )}
            </div>
            <ConfigPanel onConfigSave={handleConfigSave} config={config} />
          </div>
        </div>

        <div className="analytics-section">
          <div className="analytics-card">
            <ActivityAnalysis
              activityRate={analyticData.activityRate}
              analysisData={results}
            />
          </div>

          <div className="background-tasks-card">
            <BackgroundTaskManager />
          </div>

          <div className="background-test-card">
            <BackgroundProcessingTest />
          </div>

          {results.length > 0 && (
            <div className="actions-card">
              <button onClick={clearAllResults} className="clear-button">
                Clear Current Results
              </button>
            </div>
          )}
        </div>
      </div>

      <style jsx="true">{`
        .settings-toggles {
          display: flex;
          gap: 0.75rem;
          margin-bottom: 1rem;
          flex-wrap: wrap;
        }
        .settings-toggle-button {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 1rem;
          border-radius: 8px;
          font-size: 0.875rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
          background-color: #f5f3ff;
          border: 1px solid #e0e7ff;
          color: var(--primary-color);
        }
        .settings-toggle-button:hover {
          background-color: #e0e7ff;
        }
        .settings-icon {
          font-size: 1rem;
        }
        .service-selector-section {
          margin-bottom: 1.5rem;
        }
        .service-selector-section h3 {
          font-size: 1rem;
          font-weight: 600;
          margin-bottom: 0.75rem;
        }
        .service-options {
          display: flex;
          gap: 1rem;
        }
        .service-option {
          padding: 0.75rem 1.25rem;
          border-radius: 8px;
          background-color: white;
          border: 2px solid #e5e7eb;
          cursor: pointer;
          font-size: 0.875rem;
          font-weight: 500;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        .service-option:hover {
          border-color: var(--primary-light);
          background-color: #f5f3ff;
        }
        .service-option.selected {
          border-color: var(--primary-color);
          background-color: #f5f3ff;
        }
        .service-icon {
          color: var(--primary-color);
        }
        .session-input-container {
          display: flex;
          align-items: flex-start;
          gap: 0.75rem;
        }
        .input-wrapper {
          display: flex;
          flex-direction: column;
          min-width: 250px;
        }
        .form-input {
          padding: 0.5rem;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          font-size: 0.875rem;
          width: 100%;
        }
        .session-warning {
          display: flex;
          align-items: center;
          gap: 0.25rem;
          font-size: 0.75rem;
          color: #b91c1c;
          margin-top: 0.25rem;
        }
        .warning-icon {
          font-size: 0.75rem;
        }
        .ready-content {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
        }
        .analyze-button {
          padding: 0.5rem 1rem;
          background-color: var(--primary-color);
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 0.875rem;
          font-weight: 500;
          cursor: pointer;
          transition: background-color 0.2s;
          white-space: nowrap;
        }
        .analyze-button:hover {
          background-color: var(--primary-dark);
        }
        .session-data-section {
          background-color: white;
          border-radius: 8px;
          padding: 1.25rem;
          margin-bottom: 1.5rem;
          box-shadow: var(--shadow-sm);
        }
        .session-data-section .section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
        }
        .session-data-section h3 {
          font-size: 1rem;
          font-weight: 600;
          margin: 0;
          color: var(--text-dark);
        }
        .data-count {
          font-size: 0.875rem;
          color: var(--text-light);
          background-color: #f3f4f6;
          padding: 0.25rem 0.75rem;
          border-radius: 12px;
        }
        .leads-preview {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }
        .lead-preview-item {
          padding: 0.75rem;
          background-color: #f9fafb;
          border-radius: 6px;
          border-left: 3px solid var(--primary-color);
        }
        .lead-info {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }
        .lead-name {
          font-weight: 600;
          color: var(--text-dark);
          font-size: 0.875rem;
        }
        .lead-details {
          font-size: 0.8rem;
          color: var(--text-light);
        }
        .company {
          font-weight: 500;
        }
        .job-title {
          color: var(--text-light);
        }
        .lead-contact {
          font-size: 0.75rem;
          color: var(--text-light);
        }
        .email {
          color: var(--primary-color);
        }
        .website {
          color: var(--text-light);
        }
        .more-leads-indicator {
          text-align: center;
          padding: 0.5rem;
          color: var(--text-light);
          font-size: 0.875rem;
          font-style: italic;
        }
        .analyze-button:disabled {
          background-color: #e5e7eb;
          color: #9ca3af;
          cursor: not-allowed;
        }

        @media (max-width: 640px) {
          .session-input-container {
            flex-direction: column;
            width: 100%;
            gap: 0.5rem;
          }
          .input-wrapper {
            width: 100%;
          }
          .analyze-button {
            width: 100%;
          }
          .service-options {
            flex-direction: column;
          }
          .settings-toggles {
            flex-direction: column;
          }
          .settings-toggle-button {
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

export default Dashboard;